import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosHelper from "../../Common/AxiosHelper";
import { ApiPaths } from "../../Config/ApiPath";
import moment from "moment/moment";
import "./SupportApp.css";
import { ArrowLeft, Send, Headphones, Lock, Paperclip, AlertTriangle, Plus, ChevronDown } from "lucide-react";

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_SERVER_URL;

const isClosed = (s) => String(s || "").toLowerCase() === "closed";

// merge comments by commentId so socket echoes and API results never duplicate
const mergeComments = (prev, incoming) => {
  const seen = new Set(prev.map((c) => c?.commentId).filter(Boolean));
  const add = (incoming || []).filter((c) => c && (!c.commentId || !seen.has(c.commentId)));
  return add.length ? [...prev, ...add] : prev;
};

export default function Tickets() {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendError, setSendError] = useState("");
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [sending, setSending] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { AxiosPost, AxiosGet } = useAxiosHelper();
  const navigate = useNavigate();
  const commentsContainerRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ticketId = queryParams.get("ticketId");
  const status = queryParams.get("status");
  const reconnectTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    if (ticketId) {
      FetchTickets(ticketId);
    } else {
      setError("Ticket ID is missing");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // keep the newest message in view
  useEffect(() => {
    const el = commentsContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments, loading]);

  const FetchTickets = async (id) => {
    try {
      setLoading(true);
      const res = await AxiosGet(`${ApiPaths?.getTickets}?ticketId=${id}`);
      const t = Array.isArray(res) ? res[0] : null;
      if (!t) {
        setError("We couldn't find this ticket.");
        return;
      }
      setTicketData(t);
      setComments(t.comments || []);
    } catch (e) {
      console.log(e);
      setError("Failed to fetch ticket data");
    } finally {
      setLoading(false);
    }
  };

  const initializeWebSocket = (id) => {
    if (!SOCKET_SERVER_URL || !id) return;
    const ws = new WebSocket(SOCKET_SERVER_URL);
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "subscribe", ticketId: id }));
    };
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message?.commentId) {
          // a single new comment (user or admin)
          setComments((prev) => mergeComments(prev, [message]));
        } else if (Array.isArray(message?.data)) {
          setComments((prev) => mergeComments(prev, message.data));
        }
        // other broadcasts (e.g. the ticket list on page load) are ignored
      } catch (e) {
        console.error("Error parsing WebSocket message:", e);
      }
    };
    ws.onerror = (e) => console.error("WebSocket error:", e);
    ws.onclose = () => {
      if (!unmountedRef.current) reconnectWebSocket(id);
    };
    wsRef.current = ws;
  };

  const reconnectWebSocket = (id) => {
    if (reconnectTimeoutRef.current) return;
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      initializeWebSocket(id);
    }, 5000);
  };

  useEffect(() => {
    unmountedRef.current = false;
    if (ticketId) initializeWebSocket(ticketId);
    return () => {
      unmountedRef.current = true;
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const handleSendComment = async () => {
    const text = newComment.trim();
    if (!text || sending) return;
    setSendError("");
    try {
      setSending(true);
      const response = await AxiosPost(`${ApiPaths?.addComment}`, { ticketId, message: text });
      // the API returns the updated ticket — use it so the message shows even if the socket is down
      if (Array.isArray(response?.ticket?.comments)) {
        setComments((prev) => mergeComments(prev, response.ticket.comments));
      }
      setNewComment("");
      inputRef.current?.focus();
    } catch (e) {
      console.log("Error adding comment", e);
      setSendError(e?.response?.data?.error || "Message not sent. Tap send to try again.");
    } finally {
      setSending(false);
    }
  };

  // group messages by day
  const groups = comments.reduce((acc, c) => {
    const key = moment(c?.timestamp).format("YYYY-MM-DD");
    const g = acc[acc.length - 1];
    if (g && g.key === key) g.items.push(c);
    else acc.push({ key, items: [c] });
    return acc;
  }, []);
  const dayLabel = (key) => {
    const m = moment(key, "YYYY-MM-DD");
    if (m.isSame(moment(), "day")) return "Today";
    if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    return m.format("DD MMM YYYY");
  };

  const closed = isClosed(ticketData?.status || status);
  const statusLabel = ticketData?.status || (closed ? "Closed" : "Open");

  return (
    <section className="spChat">
      {/* ---------- Header ---------- */}
      <header className="spChatHead">
        <button type="button" className="spBack" onClick={() => navigate("/support")} aria-label="Back to support">
          <ArrowLeft size={19} />
        </button>
        <span className="spAgent">
          <Headphones size={18} />
        </span>
        <button type="button" className="spChatTitle" onClick={() => setShowDetails((v) => !v)} aria-expanded={showDetails}>
          <b>{ticketData?.subject || "Support chat"}</b>
          <small>
            <i className={`dot ${closed ? "off" : "on"}`} /> {statusLabel} · Support team
            <ChevronDown size={13} className={showDetails ? "isOpen" : ""} />
          </small>
        </button>
      </header>

      {showDetails && ticketData && (
        <div className="spDetails">
          <p>{ticketData.description}</p>
          <div>
            <span>#{String(ticketData.ticketId || "").slice(0, 8)}</span>
            <span>Opened {moment(ticketData.created_at).format("DD MMM YYYY")}</span>
            {ticketData.priority && <span>Priority: {ticketData.priority}</span>}
          </div>
          {ticketData.attachments?.length > 0 && (
            <div className="spDetailFiles">
              {ticketData.attachments.map((a, i) => (
                <a key={i} href={a} target="_blank" rel="noopener noreferrer">
                  <Paperclip size={12} /> Attachment {i + 1}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- Messages ---------- */}
      <div className="spMessages" ref={commentsContainerRef}>
        {loading ? (
          <div className="spChatState">
            <span className="spSpin dark" /> Loading conversation…
          </div>
        ) : error ? (
          <div className="spChatState">
            <AlertTriangle size={22} />
            <b>{error}</b>
            <button type="button" onClick={() => navigate("/support")}>
              Back to support
            </button>
          </div>
        ) : (
          <>
            {/* the original request opens the thread */}
            {ticketData?.description && (
              <div className="spMsgRow mine">
                <div className="spBubble mine first">
                  <em>{ticketData.subject}</em>
                  <p>{ticketData.description}</p>
                  <time>{moment(ticketData.created_at).format("DD MMM, HH:mm")}</time>
                </div>
              </div>
            )}

            {comments.length === 0 && (
              <div className="spSystem">Our support team will reply here. You'll see new messages instantly.</div>
            )}

            {groups.map((g) => (
              <div key={g.key}>
                <div className="spDay">
                  <span>{dayLabel(g.key)}</span>
                </div>
                {g.items.map((c, i) => {
                  const mine = c?.user_type !== "admin";
                  return (
                    <div key={c?.commentId || `${g.key}-${i}`} className={`spMsgRow ${mine ? "mine" : "theirs"}`}>
                      {!mine && (
                        <span className="spMsgAvatar">
                          <Headphones size={14} />
                        </span>
                      )}
                      <div className={`spBubble ${mine ? "mine" : "theirs"}`}>
                        <p>{c?.message}</p>
                        <time>{moment(c?.timestamp).format("HH:mm")}</time>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>

      {/* ---------- Composer ---------- */}
      {!loading && !error &&
        (closed ? (
          <div className="spClosed">
            <Lock size={16} />
            <span>This ticket was closed by support.</span>
            <button type="button" onClick={() => navigate("/support")}>
              <Plus size={14} /> New ticket
            </button>
          </div>
        ) : (
          <div className="spComposer">
            {sendError && (
              <p className="spSendError">
                <AlertTriangle size={13} /> {sendError}
              </p>
            )}
            <div className="spComposerRow">
              <textarea
                ref={inputRef}
                value={newComment}
                onChange={(e) => {
                  setSendError("");
                  setNewComment(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Enter sends on desktop; Shift+Enter adds a new line
                  if (e.key === "Enter" && !e.shiftKey && window.innerWidth > 800) {
                    e.preventDefault();
                    handleSendComment();
                  }
                }}
                placeholder="Type a message…"
                rows={1}
              />
              <button
                type="button"
                className="spSend"
                onClick={handleSendComment}
                disabled={!newComment.trim() || sending}
                aria-label="Send message"
              >
                {sending ? <span className="spSpin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        ))}
    </section>
  );
}
